import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ClientEnquiry } from '@/types/enquiry';

/** One client-visible enquiry (the portal path) — team/owner scoping is
 * server-side. */
export function useClientEnquiry(enquiryId: string | undefined) {
  const [enquiry, setEnquiry] = useState<ClientEnquiry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!enquiryId) {
      setEnquiry(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setEnquiry(await vnextApiClient.getClientEnquiry(enquiryId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load the enquiry');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [enquiryId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { enquiry, isLoading, error, refetch: fetchOnce };
}
